import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MapPin, Briefcase, FileText, Users, Building2, ChevronRight, Loader2 } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Firma belgeleri
const COMPANY_DOCS = [
  { key: 'sgk_hizmet_listesi', label: 'SGK Hizmet Listesi' },
  { key: 'isg_katip_uzman', label: 'İSG Katip Uzman' },
  { key: 'isg_katip_hekim', label: 'İSG Katip Hekim' },
];

// Personel belgeleri
const EMPLOYEE_DOCS = [
  { key: 'sgk_ise_giris', label: 'SGK İşe Giriş' },
  { key: 'isg_egitimi', label: 'İSG Eğitimi' },
  { key: 'saglik_raporu', label: 'Sağlık Raporu' },
  { key: 'mesleki_yeterlilik', label: 'Mesleki Yeterlilik (MYK)' },
  { key: 'mesleki_egitim', label: 'Mesleki Eğitim' },
  { key: 'kkd_zimmet', label: 'KKD Zimmet' },
];

const WorkAreasPage = () => {
  const { hasLocationAccess, canCreate, canEdit, canDelete } = useAuth();
  
  const [workAreas, setWorkAreas] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // İş Tipi ve İş Çeşidi verileri
  const [jobTypes, setJobTypes] = useState([]);
  const [jobKinds, setJobKinds] = useState([]);
  const [filteredJobKinds, setFilteredJobKinds] = useState([]);
  
  // Filters
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('all');
  
  // Form data - çoklu lokasyon desteği
  const [formData, setFormData] = useState({
    location_ids: [], // Çoklu lokasyon
    job_type_id: '',
    job_kind_id: '',
    is_tipi: '',
    is_cesidi: '',
    required_company_docs: [],
    required_employee_docs: [],
    optional_company_docs: [],
    optional_employee_docs: [],
    approver_id: '',
    store_list: false
  });
  
  // 2. Onaycılar
  const [approvers, setApprovers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchWorkAreas();
  }, [selectedLocationFilter]);

  // İş Tipi değiştiğinde İş Çeşitlerini filtrele
  useEffect(() => {
    if (formData.job_type_id) {
      const filtered = jobKinds.filter(k => k.job_type_id === formData.job_type_id);
      setFilteredJobKinds(filtered);
      // İş Tipi değiştiğinde is_tipi'yi de güncelle
      const selectedType = jobTypes.find(t => t.id === formData.job_type_id);
      if (selectedType) {
        setFormData(prev => ({ ...prev, is_tipi: selectedType.name, job_kind_id: '', is_cesidi: '' }));
      }
    } else {
      setFilteredJobKinds([]);
    }
  }, [formData.job_type_id, jobKinds, jobTypes]);

  // İş Çeşidi değiştiğinde is_cesidi'yi güncelle
  useEffect(() => {
    if (formData.job_kind_id) {
      const selectedKind = jobKinds.find(k => k.id === formData.job_kind_id);
      if (selectedKind) {
        setFormData(prev => ({ ...prev, is_cesidi: selectedKind.name }));
      }
    }
  }, [formData.job_kind_id, jobKinds]);

  const fetchData = async () => {
    try {
      const [locationsRes, areasRes, approversRes, jobTypesRes, jobKindsRes] = await Promise.all([
        axios.get(`${API}/locations`),
        axios.get(`${API}/work-areas`),
        axios.get(`${API}/users/approvers`),
        axios.get(`${API}/job-types`),
        axios.get(`${API}/job-kinds`)
      ]);
      setLocations(locationsRes.data);
      setWorkAreas(areasRes.data);
      setApprovers(approversRes.data);
      setJobTypes(jobTypesRes.data);
      setJobKinds(jobKindsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkAreas = async () => {
    try {
      const params = selectedLocationFilter !== 'all' ? `?location_id=${selectedLocationFilter}` : '';
      const response = await axios.get(`${API}/work-areas${params}`);
      setWorkAreas(response.data);
    } catch (error) {
      toast.error('Çalışma alanları yüklenemedi');
    }
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || '-';
  };

  const authorizedLocations = locations.filter(loc => hasLocationAccess(loc.id));

  const filteredAreas = workAreas.filter(area => {
    if (!hasLocationAccess(area.location_id)) return false;
    if (selectedLocationFilter !== 'all' && area.location_id !== selectedLocationFilter) return false;
    return true;
  });

  // Onaycı adını bul
  const getApproverName = (approverId) => {
    const approver = approvers.find(a => a.id === approverId);
    return approver ? `${approver.name || ''} ${approver.surname || ''}`.trim() || approver.email : '-';
  };

  const handleOpenDialog = (area = null) => {
    if (area) {
      // Düzenleme modu - mevcut iş tipi/çeşidi için ID'leri bul
      const matchingType = jobTypes.find(t => t.name === area.is_tipi);
      const matchingKind = jobKinds.find(k => k.name === area.is_cesidi && k.job_type_id === matchingType?.id);
      
      setEditingArea(area);
      setFormData({
        location_ids: [area.location_id], // Düzenlemede tek lokasyon
        job_type_id: matchingType?.id || '',
        job_kind_id: matchingKind?.id || '',
        is_tipi: area.is_tipi,
        is_cesidi: area.is_cesidi,
        required_company_docs: area.required_company_docs || [],
        required_employee_docs: area.required_employee_docs || [],
        optional_company_docs: area.optional_company_docs || [],
        optional_employee_docs: area.optional_employee_docs || [],
        approver_id: area.approver_id || '',
        store_list: !!area.store_list
      });
    } else {
      // Yeni ekleme modu
      setEditingArea(null);
      setFormData({
        location_ids: selectedLocationFilter !== 'all' ? [selectedLocationFilter] : [],
        job_type_id: '',
        job_kind_id: '',
        is_tipi: '',
        is_cesidi: '',
        required_company_docs: [],
        required_employee_docs: [],
        optional_company_docs: [],
        optional_employee_docs: [],
        approver_id: '',
        store_list: false
      });
    }
    setDialogOpen(true);
  };

  // Lokasyon checkbox toggle
  const handleLocationToggle = (locationId) => {
    const current = formData.location_ids || [];
    const updated = current.includes(locationId)
      ? current.filter(id => id !== locationId)
      : [...current, locationId];
    setFormData({ ...formData, location_ids: updated });
  };

  const handleCompanyDocToggle = (docKey, isOptional = false) => {
    if (isOptional) {
      const current = formData.optional_company_docs || [];
      const updated = current.includes(docKey) 
        ? current.filter(k => k !== docKey) 
        : [...current, docKey];
      // Opsiyonele eklenirse zorunludan çıkar
      const required = formData.required_company_docs.filter(k => k !== docKey);
      setFormData({ ...formData, optional_company_docs: updated, required_company_docs: required });
    } else {
      const current = formData.required_company_docs || [];
      const updated = current.includes(docKey) 
        ? current.filter(k => k !== docKey) 
        : [...current, docKey];
      // Zorunluya eklenirse opsiyonelden çıkar
      const optional = formData.optional_company_docs.filter(k => k !== docKey);
      setFormData({ ...formData, required_company_docs: updated, optional_company_docs: optional });
    }
  };

  const handleEmployeeDocToggle = (docKey, isOptional = false) => {
    if (isOptional) {
      const current = formData.optional_employee_docs || [];
      const updated = current.includes(docKey) 
        ? current.filter(k => k !== docKey) 
        : [...current, docKey];
      // Opsiyonele eklenirse zorunludan çıkar
      const required = formData.required_employee_docs.filter(k => k !== docKey);
      setFormData({ ...formData, optional_employee_docs: updated, required_employee_docs: required });
    } else {
      const current = formData.required_employee_docs || [];
      const updated = current.includes(docKey) 
        ? current.filter(k => k !== docKey) 
        : [...current, docKey];
      // Zorunluya eklenirse opsiyonelden çıkar
      const optional = formData.optional_employee_docs.filter(k => k !== docKey);
      setFormData({ ...formData, required_employee_docs: updated, optional_employee_docs: optional });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.is_tipi.trim() || !formData.is_cesidi.trim()) {
      toast.error('İş Tipi ve İş Çeşidi zorunludur');
      return;
    }

    if (!editingArea && formData.location_ids.length === 0) {
      toast.error('En az bir lokasyon seçmelisiniz');
      return;
    }

    setSaving(true);
    try {
      if (editingArea) {
        // Düzenleme - tek lokasyon için güncelle
        const updateData = {
          is_tipi: formData.is_tipi,
          is_cesidi: formData.is_cesidi,
          required_company_docs: formData.required_company_docs,
          required_employee_docs: formData.required_employee_docs,
          optional_company_docs: formData.optional_company_docs,
          optional_employee_docs: formData.optional_employee_docs,
          approver_id: formData.approver_id,
          store_list: formData.store_list
        };
        await axios.put(`${API}/work-areas/${editingArea.id}`, updateData);
        toast.success('Çalışma alanı güncellendi');
      } else {
        // Yeni ekleme - her lokasyon için ayrı kayıt oluştur
        const createPromises = formData.location_ids.map(locationId => {
          const createData = {
            location_id: locationId,
            is_tipi: formData.is_tipi,
            is_cesidi: formData.is_cesidi,
            required_company_docs: formData.required_company_docs,
            required_employee_docs: formData.required_employee_docs,
            optional_company_docs: formData.optional_company_docs,
            optional_employee_docs: formData.optional_employee_docs,
            approver_id: formData.approver_id,
          store_list: formData.store_list
          };
          return axios.post(`${API}/work-areas`, createData);
        });
        
        await Promise.all(createPromises);
        toast.success(`${formData.location_ids.length} lokasyon için çalışma alanı eklendi`);
      }
      setDialogOpen(false);
      fetchWorkAreas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Çalışma alanı silinsin mi?', description: 'Bu çalışma alanını silmek istediğinizden emin misiniz?', confirmLabel: 'Evet, Sil' }))) return;
    
    try {
      await axios.delete(`${API}/work-areas/${id}`);
      toast.success('Çalışma alanı silindi');
      fetchWorkAreas();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="work-areas-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Çalışma Alanları</h1>
          <p className="text-slate-500 mt-1">Her lokasyon için iş tipi ve çeşidine göre zorunlu belgeleri tanımlayın</p>
        </div>
        {canCreate('work_areas') && (
          <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800" data-testid="add-work-area-btn">
            <Plus className="h-4 w-4 mr-2" />
            Çalışma Alanı Ekle
          </Button>
        )}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Label>İşletme:</Label>
          <Select value={selectedLocationFilter} onValueChange={setSelectedLocationFilter}>
            <SelectTrigger className="w-56" data-testid="location-filter">
              <SelectValue placeholder="İşletme seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm İşletmeler</SelectItem>
              {authorizedLocations.map((location) => (
                <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">İşletme</TableHead>
                <TableHead className="font-semibold">İş Tipi</TableHead>
                <TableHead className="font-semibold">İş Çeşidi</TableHead>
                <TableHead className="font-semibold">2. Onaycı</TableHead>
                <TableHead className="font-semibold">Firma Belgeleri</TableHead>
                <TableHead className="font-semibold">Personel Belgeleri</TableHead>
                <TableHead className="font-semibold text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAreas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Briefcase className="h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">Henüz çalışma alanı tanımlanmamış</p>
                      <Button variant="outline" size="sm" onClick={() => handleOpenDialog()}>
                        İlk çalışma alanını ekle
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAreas.map((area) => (
                  <TableRow key={area.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        {getLocationName(area.location_id)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-blue-100 text-blue-700">{area.is_tipi}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-purple-100 text-purple-700">{area.is_cesidi}</Badge>
                      {area.store_list && <Badge className="ml-1 bg-amber-100 text-amber-800" data-testid={`area-store-list-badge-${area.id}`}>Mağaza listesi</Badge>}
                    </TableCell>
                    <TableCell>
                      {area.approver_id ? (
                        <span className="text-sm font-medium text-indigo-700">{getApproverName(area.approver_id)}</span>
                      ) : (
                        <span className="text-slate-400 text-xs">Tanımlı değil</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(area.required_company_docs || []).length === 0 && (area.optional_company_docs || []).length === 0 ? (
                          <span className="text-slate-400 text-xs">Belirtilmemiş</span>
                        ) : (
                          <>
                            {(area.required_company_docs || []).map(docKey => {
                              const doc = COMPANY_DOCS.find(d => d.key === docKey);
                              return doc ? (
                                <span key={docKey} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700 border border-emerald-300">
                                  {doc.label}
                                </span>
                              ) : null;
                            })}
                            {(area.optional_company_docs || []).map(docKey => {
                              const doc = COMPANY_DOCS.find(d => d.key === docKey);
                              return doc ? (
                                <span key={docKey} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600 border border-dashed border-slate-300">
                                  {doc.label}
                                </span>
                              ) : null;
                            })}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(area.required_employee_docs || []).length === 0 && (area.optional_employee_docs || []).length === 0 ? (
                          <span className="text-slate-400 text-xs">Belirtilmemiş</span>
                        ) : (
                          <>
                            {(area.required_employee_docs || []).map(docKey => {
                              const doc = EMPLOYEE_DOCS.find(d => d.key === docKey);
                              return doc ? (
                                <span key={docKey} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700 border border-amber-300">
                                  {doc.label}
                                </span>
                              ) : null;
                            })}
                            {(area.optional_employee_docs || []).map(docKey => {
                              const doc = EMPLOYEE_DOCS.find(d => d.key === docKey);
                              return doc ? (
                                <span key={docKey} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600 border border-dashed border-slate-300">
                                  {doc.label}
                                </span>
                              ) : null;
                            })}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canEdit('work_areas') && (
                          <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(area)} data-testid={`edit-area-${area.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete('work_areas') && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(area.id)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-area-${area.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArea ? 'Çalışma Alanı Düzenle' : 'Yeni Çalışma Alanı'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 py-4">
              {/* Lokasyon Seçimi - Çoklu Checkbox */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-600" />
                  Lokasyonlar * {!editingArea && <span className="text-xs text-slate-400">(Birden fazla seçebilirsiniz)</span>}
                </Label>
                {editingArea ? (
                  // Düzenleme modunda sadece görüntüleme
                  <div className="p-3 bg-slate-50 rounded-lg border">
                    <Badge variant="outline" className="gap-1">
                      <MapPin className="h-3 w-3" />
                      {getLocationName(formData.location_ids[0])}
                    </Badge>
                    <p className="text-xs text-slate-400 mt-2">Düzenleme modunda lokasyon değiştirilemez</p>
                  </div>
                ) : (
                  // Yeni ekleme modunda çoklu seçim
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {authorizedLocations.length === 0 ? (
                      <p className="text-sm text-slate-400">Erişim yetkiniz olan lokasyon bulunmuyor</p>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 pb-2 border-b">
                          <Checkbox
                            id="select-all-locations"
                            checked={formData.location_ids.length === authorizedLocations.length}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({ ...formData, location_ids: authorizedLocations.map(l => l.id) });
                              } else {
                                setFormData({ ...formData, location_ids: [] });
                              }
                            }}
                          />
                          <Label htmlFor="select-all-locations" className="text-sm font-medium cursor-pointer">
                            Tümünü Seç ({authorizedLocations.length} lokasyon)
                          </Label>
                        </div>
                        {authorizedLocations.map((location) => (
                          <div key={location.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`loc-${location.id}`}
                              checked={formData.location_ids.includes(location.id)}
                              onCheckedChange={() => handleLocationToggle(location.id)}
                              data-testid={`location-checkbox-${location.id}`}
                            />
                            <Label htmlFor={`loc-${location.id}`} className="text-sm cursor-pointer">
                              {location.name}
                            </Label>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
                {formData.location_ids.length > 0 && !editingArea && (
                  <p className="text-xs text-blue-600">
                    {formData.location_ids.length} lokasyon seçildi
                  </p>
                )}
              </div>

              {/* İş Tipi ve Çeşidi - Dropdown'lardan Seçim */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>İş Tipi *</Label>
                  {jobTypes.length === 0 ? (
                    <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
                      <p className="text-sm text-amber-700">
                        Henüz iş tipi tanımlanmamış. 
                        <a href="/job-types" className="underline ml-1">İş Tipi ekleyin →</a>
                      </p>
                    </div>
                  ) : (
                    <Select 
                      value={formData.job_type_id} 
                      onValueChange={(val) => setFormData({ ...formData, job_type_id: val })}
                    >
                      <SelectTrigger data-testid="job-type-select">
                        <SelectValue placeholder="İş tipi seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-xs text-slate-400">Çalışmanın yapılacağı ana alan</p>
                </div>
                <div className="space-y-2">
                  <Label>İş Çeşidi *</Label>
                  {!formData.job_type_id ? (
                    <div className="p-3 border rounded-lg bg-slate-50">
                      <p className="text-sm text-slate-400">Önce iş tipi seçin</p>
                    </div>
                  ) : filteredJobKinds.length === 0 ? (
                    <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
                      <p className="text-sm text-amber-700">
                        Bu iş tipi için çeşit yok. 
                        <a href={`/job-kinds?type=${formData.job_type_id}`} className="underline ml-1">İş Çeşidi ekleyin →</a>
                      </p>
                    </div>
                  ) : (
                    <Select 
                      value={formData.job_kind_id} 
                      onValueChange={(val) => setFormData({ ...formData, job_kind_id: val })}
                    >
                      <SelectTrigger data-testid="job-kind-select">
                        <SelectValue placeholder="İş çeşidi seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredJobKinds.map((kind) => (
                          <SelectItem key={kind.id} value={kind.id}>
                            {kind.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-xs text-slate-400">Yapılacak işin detay kategorisi</p>
                </div>
              </div>

              {/* Hiyerarşi Gösterimi */}
              {formData.location_ids.length > 0 && formData.is_tipi && formData.is_cesidi && (
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
                    <MapPin className="h-4 w-4" />
                    {formData.location_ids.length === 1 ? (
                      <span>{getLocationName(formData.location_ids[0])}</span>
                    ) : (
                      <Badge variant="outline">{formData.location_ids.length} lokasyon</Badge>
                    )}
                    <ChevronRight className="h-4 w-4" />
                    <Badge className="bg-blue-100 text-blue-700">{formData.is_tipi}</Badge>
                    <ChevronRight className="h-4 w-4" />
                    <Badge className="bg-purple-100 text-purple-700">{formData.is_cesidi}</Badge>
                  </p>
                </div>
              )}

              {/* Firma Belgeleri */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-600" />
                  Firma Belgeleri
                </Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-slate-600">Belge</th>
                        <th className="text-center px-4 py-2 font-medium text-emerald-600 w-24">Zorunlu</th>
                        <th className="text-center px-4 py-2 font-medium text-slate-500 w-24">Opsiyonel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {COMPANY_DOCS.map((doc) => (
                        <tr key={doc.key} className="border-t">
                          <td className="px-4 py-2">{doc.label}</td>
                          <td className="text-center px-4 py-2">
                            <Checkbox
                              checked={formData.required_company_docs.includes(doc.key)}
                              onCheckedChange={() => handleCompanyDocToggle(doc.key, false)}
                            />
                          </td>
                          <td className="text-center px-4 py-2">
                            <Checkbox
                              checked={formData.optional_company_docs.includes(doc.key)}
                              onCheckedChange={() => handleCompanyDocToggle(doc.key, true)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Personel Belgeleri */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-600" />
                  Personel Belgeleri
                </Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-slate-600">Belge</th>
                        <th className="text-center px-4 py-2 font-medium text-amber-600 w-24">Zorunlu</th>
                        <th className="text-center px-4 py-2 font-medium text-slate-500 w-24">Opsiyonel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {EMPLOYEE_DOCS.map((doc) => (
                        <tr key={doc.key} className="border-t">
                          <td className="px-4 py-2">{doc.label}</td>
                          <td className="text-center px-4 py-2">
                            <Checkbox
                              checked={formData.required_employee_docs.includes(doc.key)}
                              onCheckedChange={() => handleEmployeeDocToggle(doc.key, false)}
                            />
                          </td>
                          <td className="text-center px-4 py-2">
                            <Checkbox
                              checked={formData.optional_employee_docs.includes(doc.key)}
                              onCheckedChange={() => handleEmployeeDocToggle(doc.key, true)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-400">
                  <span className="text-emerald-600 font-medium">Zorunlu:</span> Bu belgeler olmadan çalışma izni alınamaz. 
                  <span className="text-slate-500 font-medium ml-2">Opsiyonel:</span> Çalışma izni oluştururken zorunlu yapılabilir.
                </p>
              </div>

              <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3 cursor-pointer" data-testid="area-store-list-toggle">
                <Checkbox checked={formData.store_list} onCheckedChange={(v) => setFormData({ ...formData, store_list: !!v })} data-testid="area-store-list-checkbox" className="mt-0.5" />
                <span>
                  <span className="block text-sm font-medium text-slate-800">Mağaza/İşletme listesi getir</span>
                  <span className="block text-xs text-slate-500">Firma bu alan için talep oluştururken lokasyondaki mağazalardan çalışacağı mağaza/işletmeyi seçmek zorunda olur. Talep onaylanınca mağazaya e-posta ile bilgi gider.</span>
                </span>
              </label>

              {/* 2. Onaycı Seçimi */}
              <div className="space-y-2 pt-4 border-t">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-indigo-600" />
                  2. Onaycı (İsteğe Bağlı)
                </Label>
                <Select 
                  value={formData.approver_id || 'none'} 
                  onValueChange={(val) => setFormData({ ...formData, approver_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger data-testid="approver-select">
                    <SelectValue placeholder="Onaycı seçin (isteğe bağlı)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Onaycı Yok (Doğrudan Onay)</SelectItem>
                    {approvers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name || user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">
                  2. onaycı seçilirse, bu alan için oluşturulan çalışma izinleri önce onay bekliyor durumuna geçer. 
                  Onaycı seçilmezse izinler doğrudan aktif olur.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                İptal
              </Button>
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800" 
                data-testid="save-area-btn"
                disabled={saving || !formData.is_tipi || !formData.is_cesidi || (!editingArea && formData.location_ids.length === 0)}
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingArea ? 'Güncelle' : `${formData.location_ids.length > 1 ? formData.location_ids.length + ' Lokasyon için ' : ''}Ekle`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkAreasPage;
